import React, { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';

import { queryClient } from 'api';
import { TimeOffsetRelation } from 'constants/enums';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { useEditSelfCheckinModal } from '@event-editor/hooks/eventEditorState';
import { updateSelfCheckinSettings } from '@event-editor/api/update-self-checkin-settings';

import { EditSelfCheckinFormModal, SelfCheckinModalFormValues } from './components';

const EditEventSelfCheckinModal: React.FC = () => {
  const event = useEditableEvent();
  const { showToast } = useToaster();

  const { isOpen, open, close } = useEditSelfCheckinModal();

  const editSelfCheckinMutation = useMutation({
    mutationFn: updateSelfCheckinSettings,
    onSuccess: (data) => {
      if (data.updateSelfCheckinSettings?.ok) {
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        queryClient.invalidateQueries({
          queryKey: eventEditorQueryKeys.eventCommunication(event.id!)
        });
        close();
      }
    },
    onError: () => {
      showToast('Self check-in settings could not be updated.', { variant: ToasterVariant.ERROR });
    }
  });

  const handleSubmit = useCallback(
    (values: SelfCheckinModalFormValues): Promise<unknown> => {
      const { allowUserCheckin } = values;

      return editSelfCheckinMutation.mutateAsync({
        input: {
          eventId: event.id!,
          allowUserCheckin,
          sendUserCheckinEmail: allowUserCheckin
            ? values.sendNotificationSettings.email
            : undefined,
          sendUserCheckinSlack: allowUserCheckin
            ? values.sendNotificationSettings.slack
            : undefined,
          sendUserCheckinSchedule: allowUserCheckin
            ? {
                quantity: values.schedule.quantity,
                unit: values.schedule.unit.toUpperCase(),
                beforeEventBegins: values.schedule.relation === TimeOffsetRelation.BEFORE
              }
            : null
        }
      });
    },
    [event.id]
  );

  return (
    <EditSelfCheckinFormModal
      open={isOpen}
      onOpenChange={(nextOpen) => (nextOpen ? open() : close())}
      onSubmit={handleSubmit}
    />
  );
};

export { EditEventSelfCheckinModal };
